import DueDiligenceItemEditModal from '@app/components/DueDiligenceItemEdit'
import { IDueDiligenceItem } from '@app/models/DueDiligenceItem'
import {
  DevelopmentPipelineType,
  DevelopmentSiteStatus,
} from '@app/models/enums'
import { bindFunctions } from '@app/utils/class'
import { formatAddressSingleLine } from '@app/utils/formatting'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { Alert } from 'reactstrap'
import DevelopmentPipeline from '../../../../../../components/DevelopmentPipeline/DevelopmentPipeline'
import { DevelopmentSiteViewPageProps } from './DevelopmentSiteViewPage'

type PipelineItems = { [K in DevelopmentPipelineType]?: IDueDiligenceItem[] }

export default class DevelopmentSitePipeline extends React.Component<
  DevelopmentSiteViewPageProps
> {
  constructor(props: DevelopmentSiteViewPageProps) {
    super(props)
    bindFunctions(this)
  }

  public componentDidMount() {
    this.props.loadDueDiligenceItems(this.props.site.id)
  }

  public componentDidUpdate(prevProps: DevelopmentSiteViewPageProps) {
    if (prevProps.site.id !== this.props.site.id) {
      this.props.loadDueDiligenceItems(this.props.site.id)
    }
  }

  public shouldComponentUpdate(nextProps: DevelopmentSiteViewPageProps) {
    if (this.props.site.id !== nextProps.site.id) {
      return true
    } else if (this.props.dueDiligenceItems !== nextProps.dueDiligenceItems) {
      return true
    }

    return false
  }

  public render() {
    const renderBody = this.props.isLoading
      ? this._renderLoadingBody
      : this._renderPipelineBody

    return (
      <DocumentTitle
        title={`${this.props.site.client.name} :: ${formatAddressSingleLine(
          this.props.site.address,
        )} :: Critical Dates`}
      >
        {renderBody()}
      </DocumentTitle>
    )
  }

  private _renderLoadingBody() {
    return <div className='text-center'>Retrieving pipeline</div>
  }

  private _renderPipelineBody() {
    if (!this.props.dueDiligenceItems) {
      return (
        <Alert color='danger'>
          <div>
            We are not able to load the pipeline right now. Please try back
            later.
          </div>
          {this.props.error && <div>{this.props.error}</div>}
        </Alert>
      )
    }

    const pipelines: PipelineItems = this.props.dueDiligenceItems
      .toArray()
      .reduce((grouped: PipelineItems, item: IDueDiligenceItem) => {
        if (Array.isArray(grouped[item.pipelineType])) {
          grouped[item.pipelineType]!.push(item)
        } else {
          grouped[item.pipelineType] = [item]
        }

        return grouped
      }, {})

    const showCompleted =
      this.props.site.status === DevelopmentSiteStatus.Completed
    const showActive =
      showCompleted || this.props.site.status === DevelopmentSiteStatus.Active
    const showPending =
      showActive || this.props.site.status === DevelopmentSiteStatus.Pending

    return (
      <div>
        {showCompleted && pipelines.Completed && (
          <DevelopmentPipeline
            addItem={() =>
              this.props.loadAddDueDiligenceItem(
                DevelopmentPipelineType.Completed,
                this.props.site,
              )
            }
            className='mb-4'
            deleteItem={this.props.deleteDueDiligenceItem}
            editItem={this.props.loadEditDueDiligenceItem}
            isCurrent={
              this.props.site.status === DevelopmentSiteStatus.Completed
            }
            items={pipelines.Completed}
            title='Completed Pipeline'
            user={this.props.user}
          />
        )}
        {showActive && pipelines.Active && (
          <DevelopmentPipeline
            addItem={() =>
              this.props.loadAddDueDiligenceItem(
                DevelopmentPipelineType.Active,
                this.props.site,
              )
            }
            className='mb-4'
            deleteItem={this.props.deleteDueDiligenceItem}
            editItem={this.props.loadEditDueDiligenceItem}
            isCurrent={this.props.site.status === DevelopmentSiteStatus.Active}
            items={pipelines.Active}
            title='Active Pipeline'
            user={this.props.user}
          />
        )}
        {showPending && pipelines.Pending && (
          <DevelopmentPipeline
            addItem={() =>
              this.props.loadAddDueDiligenceItem(
                DevelopmentPipelineType.Pending,
                this.props.site,
              )
            }
            className='mb-4'
            deleteItem={this.props.deleteDueDiligenceItem}
            editItem={this.props.loadEditDueDiligenceItem}
            isCurrent={this.props.site.status === DevelopmentSiteStatus.Pending}
            items={pipelines.Pending}
            title='Pending Pipeline'
            user={this.props.user}
          />
        )}
        {pipelines.Potential && (
          <DevelopmentPipeline
            addItem={() =>
              this.props.loadAddDueDiligenceItem(
                DevelopmentPipelineType.Potential,
                this.props.site,
              )
            }
            className='mb-4'
            deleteItem={this.props.deleteDueDiligenceItem}
            editItem={this.props.loadEditDueDiligenceItem}
            isCurrent={
              this.props.site.status === DevelopmentSiteStatus.Potential
            }
            items={pipelines.Potential}
            title='Potential Pipeline'
            user={this.props.user}
          />
        )}
        <DueDiligenceItemEditModal />
      </div>
    )
  }
}
